import './styles.css';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Datepicker, CalendarPrev, CalendarNext, CalendarNav } from '@mobiscroll/react';
import Icon from '@/assets/icons/calendar.svg';
import dayjs, { type Dayjs } from 'dayjs';
import { Alert } from '@/shared/ui';
import TextField from '@mui/material/TextField';
import dayJS from '../../helpers/dayjs';

interface IDatePickerProps {
    label: string;
    value: string;
    name: string;
    onChange: (string) => void;
    required?: boolean;
    wrapperClassName?: string;
    defaultValue?: Dayjs | null;
    disabled?: boolean;
    onError?: () => void;
    borderValue?: number;
    fontSize?: number;
    ref: React.ForwardedRef<HTMLDivElement>;
    error?: boolean;
    errorMessage?: string;
    isMobile?: boolean;
    subLabel?: string;
    testId?: string;
}

const DatePickerComponent: React.FC<IDatePickerProps> = ({
    label,
    required = false,
    isMobile = false,
    value,
    onChange,
    error,
    errorMessage,
    subLabel,
    testId = 'datepicker',
    ...rest
}) => {
    const border = error ? '1px solid red' : `1px solid rgba(0, 0, 0, ${value ? 0.7 : 0.5})`;
    const [isOpenPicker, setOpenPicker] = useState(false);
    const [localValue, setLocalValue] = useState('');

    const changeLocalValue = (e: ChangeEvent<HTMLInputElement>) => {
        const isDeleting = e.currentTarget.value.length < localValue.length;
        let inputValue = isDeleting ? localValue.substring(0, e.currentTarget.value.length) : e.currentTarget.value;

        const dateInputRegex = /^\d{0,2}(\/\d{0,2}(\/\d{0,4})?)?$/;

        const matches = inputValue.match(dateInputRegex);

        const slashAdded = inputValue.substring(0, inputValue.length - 1) + '/' + inputValue[inputValue.length - 1];

        if (!matches && !isDeleting && slashAdded.match(dateInputRegex)) inputValue = slashAdded;

        if (!inputValue.match(dateInputRegex) || inputValue.startsWith('/') || inputValue.includes('//')) return;

        const splitten = inputValue.split('/');
        const [mm, dd] = splitten;

        if (+mm && mm.length === 1 && inputValue.endsWith('/')) splitten[0] = `0${mm}`;
        if (+dd && dd.length === 1 && inputValue.endsWith('/')) splitten[1] = `0${dd}`;

        const formatted = splitten.join('/');

        setLocalValue(formatted);
        if (!formatted) {
            onChange('');
        } else {
            onChange(formatted);
        }
    };

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const localDayJSValue = useMemo(() => {
        if (dayJS(localValue).isValid()) {
            return dayJS(localValue).format('MM/DD/YYYY');
        } else {
            return '01/01/2000';
        }
    }, [localValue]);

    const calendarHeader = () => {
        return (
            <React.Fragment>
                <div className="flex flex-1 flex-row justify-between">
                    <CalendarPrev className="custom-prev" />
                    <CalendarNav className="custom-nav" />
                    <CalendarNext className="custom-next" />
                </div>
            </React.Fragment>
        );
    };

    return (
        <div
            data-testid={`${testId}-wrapper`}
            className={`align-flex-start justify-flex-start common__datepicker flex flex-col text-lg ${rest.wrapperClassName ? rest.wrapperClassName : ''}`}
        >
            {(label || subLabel) && (
                <div data-testid={`${testId}-label-sub-label-wrapper`} className="flex flex-col gap-1 pb-1">
                    {label && (
                        <div data-testid={`${testId}-label-wrapper`} className="flex items-center gap-2 text-black">
                            {required && <span data-testid={`${testId}-required`} className="text-lg font-bold text-red-500">*</span>}
                            <span data-testid={`${testId}-label`} className="font-atkinsonHyperlegible text-lg lg:text-xl">{label}</span>
                        </div>
                    )}
                    {subLabel && <span data-testid={`${testId}-sub-label`} className="font-dmSans text-base text-dark">{subLabel}</span>}
                </div>
            )}
            <div className="flex w-full flex-row gap-2">
                <TextField
                    inputProps={{ "data-testid": `${testId}-input` }}
                    value={localValue}
                    error={!!error}
                    onError={() => rest.onError?.()}
                    onChange={changeLocalValue}
                    placeholder="MM/DD/YYYY"
                    sx={{
                        border: 'none',
                        fieldset: {
                            border,
                            boxShadow: '0px 1px 4px 0px #0000001A'
                        },
                        outline: 0,
                        input: {
                            fontSize: rest.fontSize || 18,
                            ':hover': {
                                boxShadow: 'none !important'
                            },
                            '&::placeholder': {
                                textOverflow: 'ellipsis'
                            }
                        },
                        bgcolor: 'white',
                        padding: '1px 0px',
                        maxWidth: '100%',
                        width: '100%',
                        boxSizing: 'border-box',
                        borderWidth: '1px',
                        '& .MuiInputBase-input': {
                            p: 0,
                            border: 'none',
                            padding: '10px 15px',
                            paddingRight: '0px',
                            ':hover': {
                                boxShadow: 'none'
                            }
                        }
                    }}
                />

                <img className="cursor-pointer" onClick={() => setOpenPicker(true)} height={40} width={40} src={Icon} alt="Edit" />
            </div>
            <Datepicker
                controls={[isMobile ? 'date' : 'calendar']}
                touchUi={true}
                showInput={false}
                dateFormat="MM/DD/YYYY"
                showOnClick={false}
                showOnFocus={false}
                isOpen={isOpenPicker}
                onClose={() => setOpenPicker(false)}
                onClick={() => setOpenPicker(true)}
                themeVariant="light"
                theme="ios"
                display="center"
                inputComponent="input"
                cssClass="ph-no-capture"
                defaultValue={localDayJSValue}
                renderCalendarHeader={calendarHeader}
                onChange={event => {
                    if (event) {
                        const date = dayjs(event.value);
                        onChange(date.format('MM/DD/YYYY'));
                    }
                }}
            />
            {error && errorMessage && <Alert className="mt-2.5 font-dmSans lg:mt-6" label={errorMessage} />}
        </div>
    );
};

export const DatePicker = React.forwardRef<HTMLDivElement, IDatePickerProps>((props, ref) => {
    return <DatePickerComponent {...props} ref={ref} />;
});

export default DatePicker;
